import React, { useState, useEffect, useRef } from 'react';
import { fetchSuggestions } from '../services/api';
import { Search, Settings, Link as LinkIcon, Moon, Sun, ArrowRight, Loader2 } from 'lucide-react';

const Header = ({
  onSettingsClick,
  onLinksClick,
  theme, onThemeChange,
  mainSearchTerm, onMainSearchChange, onPrimaryAction, loading, isSearchDisabled,
  searchMode, onSearchModeChange,
  isDeepSearch, onToggleDeepSearch,
  refineSearchTerm, onRefineChange,
  isRefineVisible, onToggleRefine,
  user
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);
  // Tracks whether a search was just submitted so we can block the debounced
  // useEffect from reopening the suggestions dropdown after the user hits Enter.
  const searchJustSubmitted = useRef(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const term = mainSearchTerm.trim();

    // If the user just submitted a search, clear the debounce and bail out.
    // Reset the flag so normal typing resumes on the next keystroke.
    if (searchJustSubmitted.current) {
      searchJustSubmitted.current = false;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      return;
    }

    if (term.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      try {
        const results = await fetchSuggestions(term);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setActiveSuggestionIndex(-1);
      } catch (err) {}
    }, 250);
    return () => clearTimeout(debounceTimer.current);
  }, [mainSearchTerm]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Set the flag BEFORE cancelling the timer so the useEffect skips its run.
    searchJustSubmitted.current = true;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setShowSuggestions(false);
    setSuggestions([]);
    onPrimaryAction();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
        e.preventDefault();
        const selectedTerm = suggestions[activeSuggestionIndex];
        onMainSearchChange({ target: { value: selectedTerm } });
        setShowSuggestions(false);
        if (!loading) onPrimaryAction(selectedTerm);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onMainSearchChange({ target: { value: suggestion } });
    setShowSuggestions(false);
    if (!loading) onPrimaryAction(suggestion);
  };

  return (
    <header className="flex flex-col border-b px-8 pt-6 pb-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-base)' }}>
      <div className="flex items-center justify-between">
        <h1 className="text-base font-bold tracking-tight">
          <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">TCM</span>
          <span style={{ color: 'var(--text-muted)' }} className="font-normal ml-1">Search</span>
        </h1>
        <div className="flex items-center gap-0.5">
          <button onClick={onThemeChange} className="p-2 rounded-lg transition-all duration-150" style={{ color: 'var(--text-muted)' }} title="Toggle Theme">
            {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <button onClick={onLinksClick} className="p-2 rounded-lg transition-all duration-150" style={{ color: 'var(--text-muted)' }} title="Useful Links">
            <LinkIcon size={16} />
          </button>
          <button onClick={onSettingsClick} className="p-2 rounded-lg transition-all duration-150" style={{ color: 'var(--text-muted)' }} title="Settings">
            <Settings size={16} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3 w-full">
        <form onSubmit={handleFormSubmit} className="flex-1 w-full max-w-2xl relative" ref={wrapperRef}>
          <div className="flex items-center rounded-lg w-full focus-within:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] transition-all duration-200" style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)' }}>
            <Search size={16} className="ml-3" style={{ color: 'var(--text-faint)' }} />
            <input
              type="text"
              placeholder="Search all labs..."
              value={mainSearchTerm}
              onChange={onMainSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              className="bg-transparent border-none text-sm w-full focus:outline-none px-3 py-2.5"
              style={{ color: 'var(--text-primary)' }}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={isSearchDisabled && !loading}
              className={`mr-1.5 flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium transition-all duration-150 shrink-0 ${
                loading 
                  ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30 hover:bg-rose-500/25' 
                  : 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/25 disabled:opacity-30'
              }`}
            >
              {loading ? (
                <><Loader2 size={13} className="animate-spin" />Stop</>
              ) : (
                <><ArrowRight size={13} />Search</>
              )}
            </button>
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute z-10 w-full mt-1.5 rounded-lg shadow-xl shadow-black/40 overflow-hidden max-h-60 overflow-y-auto" style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)' }}>
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  className={`px-3 py-2 text-sm cursor-pointer transition-colors duration-100 ${index === activeSuggestionIndex ? 'bg-indigo-500/10' : ''}`}
                  style={{ color: index === activeSuggestionIndex ? 'var(--text-primary)' : 'var(--text-secondary)' }}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </li>
              ))}
            </ul>
          )}
        </form>

        <div className="flex items-center gap-1.5">
          {user?.role !== 'user_db' && (
            <button
              type="button"
              onClick={onSearchModeChange}
              className={`text-xs py-1.5 px-3 rounded-lg transition-all duration-150 ${searchMode === 'live' ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' : ''}`}
              style={searchMode !== 'live' ? { border: '1px solid var(--border-secondary)', color: 'var(--text-muted)' } : {}}
            >
              Live Search
            </button>
          )}
          <button
            type="button"
            onClick={onToggleDeepSearch}
            disabled={searchMode === 'live'}
            className={`text-xs py-1.5 px-3 rounded-lg transition-all duration-150 ${searchMode === 'live' ? 'opacity-40 cursor-not-allowed' : isDeepSearch ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' : ''}`}
            style={(!isDeepSearch && searchMode !== 'live') || searchMode === 'live' ? { border: '1px solid var(--border-secondary)', color: 'var(--text-muted)' } : {}}
          >
            Deep Search
          </button>
          <div className="flex items-center gap-1.5 relative">
            <button onClick={onToggleRefine} className={`text-xs py-1.5 px-3 rounded-lg transition-all duration-150 ${isRefineVisible ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' : ''}`}
              style={!isRefineVisible ? { border: '1px solid var(--border-secondary)', color: 'var(--text-muted)' } : {}}
            >
              Refine
            </button>
            {isRefineVisible && (
              <input
                type="text"
                placeholder="Filter results..."
                value={refineSearchTerm}
                onChange={onRefineChange}
                className="rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] transition-all duration-200 w-48"
                style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-secondary)', color: 'var(--text-primary)' }}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;